import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Loader2,
  RefreshCw,
  Download,
  ImageIcon,
  Scissors,
  Eye,
  EyeOff,
  Wand2,
  Clapperboard,
  Upload,
  Pencil,
  AlertTriangle,
  Film,
  Play,
  Maximize2,
} from "lucide-react";

type ContentBox = { x: number; y: number; w: number; h: number };
type PanelImage = { scene_number?: number; storyboard_index?: number; image_url?: string; original_image_url?: string; content_box?: ContentBox | null };
type ScenePrompt = { scene_number?: number; storyboard_index?: number; prompt?: string; error?: string; product_featured?: boolean };
type SceneVideo = { scene_number?: number; storyboard_index?: number; prompt?: string; videoUrl?: string; status?: string; error?: string; userProvided?: boolean };

export function ScenePanelReview({
  script,
  campaignId,
  variant = "ugc",
}: {
  script: any;
  campaignId: string;
  variant?: "ugc" | "claymation" | "videoExpress" | "pixar" | "simpsons" | "dayinlife";
}) {
  const { toast } = useToast();
  // This component is shared by the UGC, Claymation, and Pixar campaign detail
  // pages. The resource bases must follow the variant so a claymation/pixar
  // script id is never POSTed to a /api/ugc-scripts/* route (which 404s
  // "Script not found").
  const campaignResource = variant === "claymation" ? "claymation-campaigns" : variant === "videoExpress" ? "video-express-campaigns" : variant === "pixar" ? "pixar-campaigns" : variant === "simpsons" ? "simpsons-campaigns" : variant === "dayinlife" ? "dayinlife-campaigns" : "ugc-campaigns";
  const scriptResource = variant === "claymation" ? "claymation-scripts" : variant === "videoExpress" ? "video-express-scripts" : variant === "pixar" ? "pixar-scripts" : variant === "simpsons" ? "simpsons-scripts" : variant === "dayinlife" ? "dayinlife-scripts" : "ugc-scripts";
  const scriptsKey = [`/api/${campaignResource}`, campaignId, "scripts"];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: scriptsKey });

  const panelImages: PanelImage[] = ((script.panelImages as PanelImage[]) || [])
    .slice()
    .sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0));
  const scenePrompts: ScenePrompt[] = (script.scenePrompts as ScenePrompt[]) || [];
  const sceneVideos: SceneVideo[] = (script.sceneVideos as SceneVideo[]) || [];
  const panelStatus: string = script.panelStatus || "idle";
  const panelError: string | undefined = script.panelError || undefined;

  const promptByScene = new Map<number, ScenePrompt>();
  scenePrompts.forEach((p) => { if (p.scene_number != null) promptByScene.set(p.scene_number, p); });
  const videoByScene = new Map<number, SceneVideo>();
  sceneVideos.forEach((v) => { if (v.scene_number != null) videoByScene.set(v.scene_number, v); });

  const [edits, setEdits] = useState<Record<number, string>>({});
  const [motionEdits, setMotionEdits] = useState<Record<number, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  // Which scene's reference image is enlarged. Controlled so the same dialog can
  // be opened from the media tile (no clip) or the Edit popover (clip present)
  // without nesting a Radix Dialog inside the Popover.
  const [enlargeFor, setEnlargeFor] = useState<number | null>(null);
  // Custom video chrome: hide native controls until the user starts a clip so
  // tiles read as a clean poster + glassy play button (not a raw HTML5 player).
  const [playing, setPlaying] = useState<Record<string, boolean>>({});
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});

  // Per-storyboard-slot debug info: the original grid image + every detected
  // frame rectangle (normalized 0..1) so the cuts can be verified pre-split.
  const storyboards: { idx: number; url?: string; rects: ContentBox[] }[] = [
    { idx: 1, url: script.storyboardImageUrl },
    { idx: 2, url: script.storyboardImageUrl2 },
    { idx: 3, url: script.storyboardImageUrl3 },
  ]
    .filter((s) => !!s.url)
    .map((s) => ({
      idx: s.idx,
      url: s.url,
      rects: panelImages
        .filter((p) => (p.storyboard_index || 1) === s.idx && p.content_box)
        .map((p) => p.content_box as ContentBox),
    }));

  const extractMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/${scriptResource}/${script.id}/extract-panels`),
    onSuccess: () => { invalidate(); toast({ title: "Re-slicing storyboard into panels…" }); },
    onError: (e: any) => toast({ title: "Failed to extract panels", description: e?.message, variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: async ({ n, instruction }: { n: number; instruction: string }) =>
      apiRequest("POST", `/api/${scriptResource}/${script.id}/scenes/${n}/edit`, { instruction, mode: "visual" }),
    onSuccess: (_d, vars) => {
      invalidate();
      setEdits((prev) => { const x = { ...prev }; delete x[vars.n]; return x; });
      toast({ title: `Editing scene ${vars.n}…`, description: "Updating the scene's reference image with your change, then re-rendering this clip." });
    },
    onError: (e: any) => toast({ title: "Failed to apply edit", description: e?.message, variant: "destructive" }),
  });

  const motionEditMutation = useMutation({
    mutationFn: async ({ n, instruction }: { n: number; instruction: string }) =>
      apiRequest("POST", `/api/${scriptResource}/${script.id}/scenes/${n}/edit`, { instruction, mode: "motion" }),
    onSuccess: (_d, vars) => {
      invalidate();
      setMotionEdits((prev) => { const x = { ...prev }; delete x[vars.n]; return x; });
      toast({ title: `Updating motion for scene ${vars.n}…`, description: "Rewriting the scene's video prompt with your action change, then re-rendering this clip." });
    },
    onError: (e: any) => toast({ title: "Failed to apply motion edit", description: e?.message, variant: "destructive" }),
  });

  const regenMutation = useMutation({
    mutationFn: async (n: number) => apiRequest("POST", `/api/${scriptResource}/${script.id}/scene-videos/${n}/regenerate`),
    onSuccess: (_d, n) => { invalidate(); toast({ title: `Regenerating clip ${n}…` }); },
    onError: (e: any) => toast({ title: "Failed to regenerate clip", description: e?.message, variant: "destructive" }),
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ n, file }: { n: number; file: File }) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/${scriptResource}/${script.id}/scenes/${n}/upload-video`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any)?.message || "Upload failed");
      }
      return res.json();
    },
    onSuccess: (_d, vars) => { invalidate(); toast({ title: `Uploaded your clip for scene ${vars.n}.` }); },
    onError: (e: any) => toast({ title: "Failed to upload clip", description: e?.message, variant: "destructive" }),
  });

  const uploadInputs = useRef<Record<number, HTMLInputElement | null>>({});

  const safeTitle = (script.title || "script").replace(/[^a-zA-Z0-9]/g, "_");
  const extractBusy = extractMutation.isPending || panelStatus === "generating";

  if (panelImages.length === 0) {
    return (
      <div className="space-y-2" data-testid={`panel-review-empty-${script.id}`}>
        {panelStatus === "failed" && panelError && (
          <p className="text-[11px] text-red-500" data-testid={`text-panel-error-${script.id}`}>
            Panel extraction failed: {panelError}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground/60">
          {extractBusy
            ? "Slicing storyboard into individual panels…"
            : "No panels yet. Extract panels from the storyboard to review and edit each scene."}
        </p>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-[11px] rounded-full"
          disabled={extractBusy}
          onClick={() => extractMutation.mutate()}
          data-testid={`button-extract-panels-${script.id}`}
        >
          {extractBusy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Extracting…</> : <><Scissors className="h-3 w-3 mr-1" /> Extract Panels</>}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid={`panel-review-${script.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
            <Clapperboard className="h-3 w-3" />
            {panelImages.length}
          </span>
          <p className="truncate text-[10px] text-muted-foreground/70">
            Describe an edit for any scene to change one detail, then re-stitch.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[10px] rounded-full"
            onClick={() => setShowPreview((v) => !v)}
            data-testid={`button-toggle-panel-preview-${script.id}`}
          >
            {showPreview ? <><EyeOff className="h-3 w-3 mr-1" /> Hide cuts</> : <><Eye className="h-3 w-3 mr-1" /> Preview cuts</>}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[10px] rounded-full"
            disabled={extractBusy}
            onClick={() => extractMutation.mutate()}
            data-testid={`button-reextract-panels-${script.id}`}
          >
            {extractBusy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Re-slicing…</> : <><Scissors className="h-3 w-3 mr-1" /> Re-extract</>}
          </Button>
        </div>
      </div>

      {/* Debug preview: all extracted panels laid out in the storyboard's
          2-column reading order so the cuts can be verified before generating. */}
      {showPreview && (
        <div
          className="rounded-lg border border-dashed border-border/40 bg-muted/[0.04] p-2.5 space-y-2"
          data-testid={`panel-preview-grid-${script.id}`}
        >
          {/* Detected frames: original grid with each detected frame rectangle
              drawn over it, so the per-frame cuts can be verified pre-split. */}
          {storyboards.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] text-muted-foreground/60">
                Detected frames (red = each extracted frame, from gutter detection):
              </p>
              <div className={`grid gap-2 ${storyboards.length > 1 ? "grid-cols-2" : "grid-cols-1 max-w-[220px]"}`}>
                {storyboards.map((sb) => (
                  <div
                    key={sb.idx}
                    className="relative rounded-md overflow-hidden border border-border/30"
                    data-testid={`storyboard-boundary-${script.id}-${sb.idx}`}
                  >
                    <img src={sb.url} alt={`Storyboard ${sb.idx}`} className="w-full block" />
                    {sb.rects.map((rect, i) => (
                      <div
                        key={i}
                        className="absolute border-2 border-red-500/90 pointer-events-none"
                        style={{
                          left: `${rect.x * 100}%`,
                          top: `${rect.y * 100}%`,
                          width: `${rect.w * 100}%`,
                          height: `${rect.h * 100}%`,
                        }}
                        data-testid={`boundary-rect-${script.id}-${sb.idx}-${i}`}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground/60">
            Extracted panels ({panelImages.length} frames) — verify each cut is one complete frame.
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {panelImages.map((panel) => {
              const n = panel.scene_number || 0;
              return (
                <div key={n} className="relative" data-testid={`panel-preview-${script.id}-${n}`}>
                  {(panel.original_image_url || panel.image_url) ? (
                    <img
                      src={panel.original_image_url || panel.image_url}
                      alt={`Panel ${n}`}
                      className="w-full rounded-md border border-border/30 object-cover aspect-[9/16]"
                      data-testid={`img-panel-preview-${n}`}
                    />
                  ) : (
                    <div className="w-full aspect-[9/16] rounded-md bg-muted/20 flex items-center justify-center">
                      <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
                    </div>
                  )}
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-semibold leading-none">
                    {n}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3"
        data-testid={`scene-grid-${script.id}`}
      >
        {panelImages.map((panel) => {
          const n = panel.scene_number || 0;
          const prompt = promptByScene.get(n);
          const video = videoByScene.get(n);
          const instruction = edits[n] || "";
          const motionInstruction = motionEdits[n] || "";
          const editingThis = editMutation.isPending && editMutation.variables?.n === n;
          const motionEditingThis = motionEditMutation.isPending && motionEditMutation.variables?.n === n;
          const regenThis = regenMutation.isPending && regenMutation.variables === n;
          const uploadingThis = uploadMutation.isPending && uploadMutation.variables?.n === n;
          const clipPending = !!video && !video.videoUrl && !video.error && video.status !== "paused";
          const busy = editingThis || motionEditingThis || regenThis || uploadingThis || clipPending;
          const hasClip = !!video?.videoUrl;
          const rendering = (regenThis || clipPending) && !editingThis && !motionEditingThis;
          const status: "ready" | "rendering" | "failed" | "draft" =
            video?.error ? "failed" : rendering ? "rendering" : hasClip ? "ready" : "draft";
          const statusDot =
            status === "ready" ? "bg-emerald-500" : status === "rendering" ? "bg-violet-500" : status === "failed" ? "bg-red-500" : "bg-muted-foreground/40";
          const statusLabel =
            status === "ready" ? "Ready" : status === "rendering" ? "Rendering" : status === "failed" ? "Failed" : "Draft";
          // Key play state by clip URL so a regenerated/replaced clip (new URL)
          // returns to poster + play-button mode instead of inheriting controls.
          const playKey = video?.videoUrl || "";

          return (
            <div
              key={n}
              className="group relative flex flex-col overflow-hidden rounded-xl border border-card-border bg-card shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
              data-testid={`panel-scene-${script.id}-${n}`}
            >
              {/* Always-mounted hidden upload input (kept outside the popover so
                  the native file dialog closing the popover never drops it). */}
              <input
                ref={(el) => { uploadInputs.current[n] = el; }}
                type="file"
                accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadMutation.mutate({ n, file });
                  e.target.value = "";
                }}
                data-testid={`input-upload-clip-${n}`}
              />

              {/* Media: the rendered clip if we have one, otherwise the scene's
                  reference panel (click to enlarge). */}
              <div className="relative aspect-[9/16] overflow-hidden bg-gradient-to-b from-muted/40 to-muted/20">
                {hasClip ? (
                  <>
                    <video
                      key={playKey}
                      ref={(el) => { videoRefs.current[n] = el; }}
                      src={video!.videoUrl}
                      poster={panel.image_url || undefined}
                      controls={!!playing[playKey]}
                      preload="none"
                      playsInline
                      onPlay={() => setPlaying((p) => ({ ...p, [playKey]: true }))}
                      onEnded={() => setPlaying((p) => ({ ...p, [playKey]: false }))}
                      className="absolute inset-0 h-full w-full object-cover"
                      data-testid={`video-scene-${n}`}
                    />
                    {!playing[playKey] && (
                      <button
                        type="button"
                        onClick={() => { const v = videoRefs.current[n]; if (v) v.play().catch(() => {}); }}
                        className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-t from-black/40 via-transparent to-black/10 transition-colors"
                        aria-label={`Play scene ${n}`}
                        data-testid={`button-play-scene-${n}`}
                      >
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white shadow-lg ring-1 ring-white/40 backdrop-blur-md transition-transform duration-200 group-hover:scale-110">
                          <Play className="h-5 w-5 translate-x-[1px] fill-white" />
                        </span>
                      </button>
                    )}
                  </>
                ) : panel.image_url ? (
                  <button
                    type="button"
                    className="absolute inset-0 h-full w-full"
                    onClick={() => setEnlargeFor(n)}
                    data-testid={`button-panel-enlarge-${n}`}
                  >
                    <img
                      src={panel.image_url}
                      alt={`Scene ${n} reference`}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      data-testid={`img-panel-${n}`}
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/30 group-hover:opacity-100">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white ring-1 ring-white/40 backdrop-blur-md">
                        <Maximize2 className="h-4 w-4" />
                      </span>
                    </span>
                  </button>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
                  </div>
                )}

                {/* Top-left: scene index + status badges */}
                <div className="pointer-events-none absolute left-2 top-2 z-20 flex flex-col gap-1">
                  <span
                    className="rounded-md bg-black/50 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white backdrop-blur-sm tabular-nums"
                    data-testid={`chip-scene-index-${n}`}
                  >
                    {String(n).padStart(2, "0")}
                  </span>
                  {prompt?.product_featured && (
                    <span
                      className="rounded-md bg-primary/90 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-primary-foreground shadow-sm"
                      data-testid={`badge-product-${n}`}
                    >
                      Product
                    </span>
                  )}
                  {hasClip && video?.userProvided && (
                    <span
                      className="rounded-md bg-black/65 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-white shadow-sm backdrop-blur-sm"
                      data-testid={`badge-user-clip-${n}`}
                    >
                      Your clip
                    </span>
                  )}
                </div>

                {/* Download (only when a clip exists) */}
                {hasClip && (
                  <a
                    href={video!.videoUrl}
                    download={`${safeTitle}_scene_${n}.mp4`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Download scene ${n}`}
                    className="absolute right-2 top-2 z-30 flex h-7 w-7 items-center justify-center rounded-md bg-black/55 text-white opacity-0 ring-1 ring-white/15 backdrop-blur-sm transition-opacity hover:bg-black/70 group-hover:opacity-100"
                    data-testid={`download-scene-${n}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                )}

                {/* Render / busy overlay */}
                {rendering ? (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-background/75 text-[10px] font-medium text-foreground backdrop-blur-sm">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    Rendering…
                  </div>
                ) : (editingThis || motionEditingThis || uploadingThis) ? (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-background/75 text-[10px] font-medium text-foreground backdrop-blur-sm">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    {editingThis ? "Applying edit…" : motionEditingThis ? "Updating motion…" : "Uploading…"}
                  </div>
                ) : null}

                {/* Failed clip ribbon */}
                {video?.error && !rendering && (
                  <div
                    className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-1 bg-gradient-to-t from-red-600/95 to-red-500/85 px-2 py-1 text-[9px] font-medium text-white"
                    title={`Clip failed: ${video.error}`}
                    data-testid={`text-scene-error-${n}`}
                  >
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    <span className="truncate">Clip failed: {video.error}</span>
                  </div>
                )}

                {/* Empty (no clip yet, not rendering) hint */}
                {!hasClip && !rendering && !video?.error && !editingThis && !motionEditingThis && !uploadingThis && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center gap-1 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-5 text-[9px] font-medium text-white/90">
                    <Film className="h-3 w-3 shrink-0" />
                    <span className="truncate">No clip yet</span>
                  </div>
                )}
              </div>

              {/* Single enlarge dialog for this card's reference image; controlled
                  so it can be opened from the media tile (no clip) or the Edit
                  popover (clip present) without nesting a Dialog inside the Popover. */}
              {panel.image_url && (
                <Dialog open={enlargeFor === n} onOpenChange={(o) => setEnlargeFor(o ? n : null)}>
                  <DialogContent className="max-w-[95vw] w-fit p-2" aria-describedby={undefined}>
                    <DialogHeader className="sr-only">
                      <DialogTitle>Scene {n} panel</DialogTitle>
                    </DialogHeader>
                    <img
                      src={panel.image_url}
                      alt={`Panel ${n}`}
                      className="mx-auto h-auto max-h-[85vh] w-auto max-w-full object-contain rounded-lg"
                      data-testid={`img-panel-large-${n}`}
                    />
                  </DialogContent>
                </Dialog>
              )}

              {/* Footer: scene label + status + Edit popover */}
              <div className="flex items-center justify-between gap-1.5 border-t border-card-border bg-card px-2.5 py-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDot} ${status === "rendering" ? "animate-pulse" : ""}`}
                    title={statusLabel}
                    data-testid={`status-scene-${n}`}
                  />
                  <span className="sr-only">{statusLabel}</span>
                  <span className="truncate text-[11px] font-semibold text-foreground" data-testid={`label-scene-${n}`}>
                    Scene {n}
                  </span>
                  <span className="hidden truncate text-[10px] text-muted-foreground/70 sm:inline" aria-hidden="true">
                    {statusLabel}
                  </span>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7 shrink-0 rounded-full px-2.5 text-[10px] font-medium"
                      data-testid={`button-edit-scene-${n}`}
                    >
                      {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Pencil className="mr-1 h-3 w-3" />}
                      Edit
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-80 space-y-3 p-3"
                    data-testid={`popover-edit-${n}`}
                  >
                    <div className="flex items-center gap-2">
                      {panel.image_url ? (
                        <button
                          type="button"
                          className="relative h-12 w-[27px] shrink-0 overflow-hidden rounded border border-border/40 transition-colors hover:border-violet-500/40"
                          onClick={() => setEnlargeFor(n)}
                          data-testid={`button-popover-enlarge-${n}`}
                        >
                          <img
                            src={panel.image_url}
                            alt={`Scene ${n} reference`}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </button>
                      ) : (
                        <div className="flex h-12 w-[27px] shrink-0 items-center justify-center rounded border border-border/40 bg-muted/20">
                          <ImageIcon className="h-3.5 w-3.5 text-muted-foreground/40" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-semibold leading-none">Scene {n}</p>
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {panel.image_url ? "Tap the image to enlarge the reference." : "Describe one change, then re-render this clip."}
                        </p>
                      </div>
                    </div>

                    {/* Visual edit: edits the scene's reference image to apply
                        only the requested look change; the clip re-renders from
                        the updated image. */}
                    <div className="space-y-1">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">Visual edit</p>
                      <Textarea
                        value={instruction}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [n]: e.target.value }))}
                        placeholder="Change how this scene looks (e.g. make the mug blue, reveal the product more)…"
                        className="min-h-[56px] resize-y text-[11px] leading-relaxed"
                        disabled={busy}
                        data-testid={`textarea-edit-${n}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-full rounded-full text-[10px]"
                        disabled={!instruction.trim() || busy}
                        onClick={() => editMutation.mutate({ n, instruction: instruction.trim() })}
                        data-testid={`button-apply-edit-${n}`}
                      >
                        {editingThis ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Applying…</> : <><Wand2 className="mr-1 h-3 w-3" /> Apply Visual Edit</>}
                      </Button>
                    </div>

                    {/* Action/motion edit: rewrites the scene's video prompt to
                        apply the requested action/motion change, then re-renders
                        the clip from the SAME reference image. */}
                    <div className="space-y-1">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">Action / motion edit</p>
                      <Textarea
                        value={motionInstruction}
                        onChange={(e) => setMotionEdits((prev) => ({ ...prev, [n]: e.target.value }))}
                        placeholder="Change the action or motion (e.g. slow zoom in, she turns to the camera, faster pace)…"
                        className="min-h-[56px] resize-y text-[11px] leading-relaxed"
                        disabled={busy}
                        data-testid={`textarea-motion-edit-${n}`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-full rounded-full text-[10px]"
                        disabled={!motionInstruction.trim() || busy}
                        onClick={() => motionEditMutation.mutate({ n, instruction: motionInstruction.trim() })}
                        data-testid={`button-apply-motion-edit-${n}`}
                      >
                        {motionEditingThis ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Rewriting…</> : <><Clapperboard className="mr-1 h-3 w-3" /> Apply Motion Edit</>}
                      </Button>
                    </div>

                    {prompt?.error && (
                      <p className="text-[10px] text-red-500" data-testid={`text-prompt-error-${n}`}>
                        Prompt error: {prompt.error}
                      </p>
                    )}

                    <div className="flex items-center gap-2 border-t border-border/40 pt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 flex-1 rounded-full text-[10px]"
                        disabled={busy}
                        onClick={() => regenMutation.mutate(n)}
                        data-testid={`button-regenerate-clip-${n}`}
                      >
                        {rendering ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Rendering…</> : <><RefreshCw className="mr-1 h-3 w-3" /> {hasClip ? "Re-render" : "Generate"} Clip</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 flex-1 rounded-full text-[10px]"
                        disabled={busy}
                        onClick={() => uploadInputs.current[n]?.click()}
                        data-testid={`button-upload-clip-${n}`}
                      >
                        {uploadingThis ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Uploading…</> : <><Upload className="mr-1 h-3 w-3" /> {hasClip ? "Replace" : "Upload"}</>}
                      </Button>
                    </div>
                    <p className="text-[9px] text-muted-foreground/50">Upload your own clip · MP4, WebM, or MOV · up to 50MB</p>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
